import React from 'react';

type MyAccountCardVariant = 'portal' | 'page';

interface MyAccountCardProps {
    slug: string;
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    content?: React.ReactNode;
    children?: React.ReactNode;
    className?: string;
    innerClassName?: string;
    contentClassName?: string;
    headerClassName?: string;
    titleClassName?: string;
    subtitleClassName?: string;
    wrapContent?: boolean;
    eyebrow?: React.ReactNode;
    meta?: React.ReactNode;
    actions?: React.ReactNode;
    as?: React.ElementType;
    ariaLabelledBy?: string;
    href?: string;
    id?: string;
    onMouseEnter?: React.MouseEventHandler<HTMLElement>;
    onMouseLeave?: React.MouseEventHandler<HTMLElement>;
    rel?: string;
    style?: React.CSSProperties;
    target?: string;
    titleAs?: React.ElementType;
    variant?: MyAccountCardVariant;
}

const normalizeSlug = (slug: string): string => (
    slug
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'card'
);

const joinClassNames = (...values: Array<string | false | null | undefined>): string => (
    values.filter(Boolean).join(' ')
);

const MyAccountCard: React.FC<MyAccountCardProps> = ({
    slug,
    title,
    subtitle,
    content,
    children,
    className = '',
    innerClassName = '',
    contentClassName = '',
    headerClassName = '',
    titleClassName = '',
    subtitleClassName = '',
    wrapContent = true,
    eyebrow,
    meta,
    actions,
    as: CardTag = 'article',
    ariaLabelledBy,
    href,
    id,
    onMouseEnter,
    onMouseLeave,
    rel,
    style,
    target,
    titleAs: TitleTag = 'h2',
    variant = 'portal',
}) => {
    const normalizedSlug = normalizeSlug(slug);
    const body = content ?? children;
    const hasHeader = Boolean(title || subtitle || meta || actions || eyebrow);
    const isPage = variant === 'page';

    const outerClassName = isPage
        ? joinClassNames('card weka-my-account-card weka-my-account-card--page weka-my-account-page__panel', `weka-my-account-card--${normalizedSlug}`, className)
        : joinClassNames('portal-panel weka-my-account-card weka-my-account-card--portal weka-dashboard-card', `weka-my-account-card--${normalizedSlug}`, `weka-dashboard-card--${normalizedSlug}`, className);

    const resolvedInnerClassName = isPage
        ? joinClassNames('card-body weka-my-account-card__inner weka-my-account-card__inner--page weka-my-account-page__body', innerClassName)
        : joinClassNames('portal-panel-container weka-my-account-card__inner weka-my-account-card__inner--portal weka-dashboard-card__inner', `weka-dashboard-card__inner--${normalizedSlug}`, innerClassName);

    const resolvedHeaderClassName = isPage
        ? joinClassNames('weka-my-account-card__header weka-my-account-page__header', headerClassName)
        : joinClassNames('weka-my-account-card__header weka-dashboard-card__header', headerClassName);

    const resolvedTitleClassName = isPage
        ? joinClassNames('weka-my-account-card__title weka-my-account-page__title', titleClassName)
        : joinClassNames('portal-panel-header weka-my-account-card__title weka-dashboard-card__title', titleClassName);

    const resolvedSubtitleClassName = isPage
        ? joinClassNames('weka-my-account-card__subtitle weka-my-account-page__subtitle', subtitleClassName)
        : joinClassNames('weka-my-account-card__subtitle weka-dashboard-card__subtitle', subtitleClassName);

    const resolvedContentClassName = isPage
        ? joinClassNames('weka-my-account-card__content weka-my-account-page__content', contentClassName)
        : joinClassNames('weka-my-account-card__content weka-dashboard-card__content', contentClassName);

    return (
        <CardTag
            id={id}
            aria-labelledby={ariaLabelledBy}
            className={outerClassName}
            href={href}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            rel={rel}
            style={style}
            target={target}
        >
            <div className={resolvedInnerClassName}>
                {hasHeader && (
                    <header className={resolvedHeaderClassName}>
                        <div className={isPage ? 'weka-my-account-page__title-group' : 'weka-my-account-card__title-group'}>
                            {eyebrow && <p className="weka-my-account-page__eyebrow">{eyebrow}</p>}
                            {title && <TitleTag className={resolvedTitleClassName}>{title}</TitleTag>}
                            {subtitle && <p className={resolvedSubtitleClassName}>{subtitle}</p>}
                            {meta && <div className="weka-my-account-page__meta">{meta}</div>}
                        </div>
                        {actions && (
                            <div className={isPage ? 'weka-my-account-page__actions' : 'weka-my-account-card__actions'}>
                                {actions}
                            </div>
                        )}
                    </header>
                )}

                {body && (
                    wrapContent
                        ? <div className={resolvedContentClassName}>{body}</div>
                        : <>{body}</>
                )}
            </div>
        </CardTag>
    );
};

export default MyAccountCard;
